import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, ArrowLeft, ExternalLink, Save, StickyNote, Instagram, Facebook, Music2 } from 'lucide-react';
import { toast } from 'sonner';
import { adminFetch } from '../utils/adminAuth';
import { formatPrice, formatDateTime } from '../lib/utils';
import { buildWhatsAppUrl } from '../utils/whatsapp';
import { resolveSocialUrl } from '../utils/socialLink';

const NETWORKS = [
  { value: 'instagram', Icon: Instagram, label: 'Instagram', activeBg: '#E4405F' },
  { value: 'facebook',  Icon: Facebook,  label: 'Facebook',  activeBg: '#1877F2' },
  { value: 'tiktok',    Icon: Music2,    label: 'TikTok',    activeBg: '#000000' },
];
import EditOrderItemsModal from '../components/admin/EditOrderItemsModal';
import ExchangeModal from '../components/admin/ExchangeModal';

const API_URL = '';

const STATUS_LABELS = {
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800' },
  en_attente: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800' },
  confirmed: { label: 'Confirmée', color: 'bg-blue-100 text-blue-800' },
  en_preparation: { label: 'En préparation', color: 'bg-indigo-100 text-indigo-800' },
  processing: { label: 'En préparation', color: 'bg-indigo-100 text-indigo-800' },
  fulfilled: { label: 'Expédiée', color: 'bg-purple-100 text-purple-800' },
  shipped: { label: 'Expédiée', color: 'bg-purple-100 text-purple-800' },
  delivered: { label: 'Livrée', color: 'bg-green-100 text-green-800' },
  livre: { label: 'Livrée', color: 'bg-green-100 text-green-800' },
  cancelled: { label: 'Annulée', color: 'bg-red-100 text-red-800' },
};

export default function AdminOrderDetail() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [showEdit, setShowEdit] = useState(false);
  const [showExchange, setShowExchange] = useState(false);
  const [socialMedia, setSocialMedia] = useState('');
  const [socialNetwork, setSocialNetwork] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [savingMeta, setSavingMeta] = useState(false);
  const [promoInput, setPromoInput] = useState('');
  const [promoBusy, setPromoBusy] = useState(false);

  const fetchOrder = async () => {
    setLoading(true);
    try {
      const r = await adminFetch(`${API_URL}/api/bloom-gestion-secure/orders/${orderId}`);
      if (r.ok) {
        const data = await r.json();
        setOrder(data);
        setSocialMedia(data.social_media_account || '');
        setSocialNetwork(data.social_media_network || '');
        setAdminNote(data.admin_note || '');
      } else {
        toast.error('Commande introuvable');
      }
    } finally { setLoading(false); }
  };

  const fetchHistory = async () => {
    try {
      const r = await adminFetch(`${API_URL}/api/bloom-gestion-secure/orders/${orderId}/history`);
      if (r.ok) {
        const d = await r.json();
        setHistory(d.history || []);
      }
    } catch (e) {}
  };

  useEffect(() => {
    fetchOrder();
    fetchHistory();
  }, [orderId]);

  const callWorkflow = async (action, body = {}) => {
    try {
      const r = await adminFetch(`${API_URL}/api/bloom-gestion-secure/orders/${orderId}/${action}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const data = await r.json();
      const labels = { confirm: 'confirmée', fulfill: 'expédiée', cancel: 'annulée' };
      toast.success(`Commande #${orderId} ${labels[action] || 'mise à jour'}`);
      if (action === 'fulfill' && data.tracking) toast.success(`Tracking : ${data.tracking}`);
      fetchOrder();
      fetchHistory();
    } catch (e) {
      toast.error(`Erreur : ${e.message}`);
    }
  };

  const saveMeta = async () => {
    setSavingMeta(true);
    try {
      const r = await adminFetch(`${API_URL}/api/bloom-gestion-secure/orders/${orderId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          social_media_account: socialMedia,
          social_media_network: socialNetwork,
          admin_note: adminNote,
        }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success('Notes enregistrées');
      fetchOrder();
    } catch (e) {
      toast.error(`Erreur : ${e.message}`);
    } finally {
      setSavingMeta(false);
    }
  };

  const applyPromo = async (codeOrNull) => {
    setPromoBusy(true);
    try {
      const r = await adminFetch(`${API_URL}/api/bloom-gestion-secure/orders/${orderId}/apply-promo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: codeOrNull }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      toast.success(codeOrNull ? `Code ${codeOrNull.toUpperCase()} appliqué` : 'Code promo retiré');
      setPromoInput('');
      fetchOrder();
      fetchHistory();
    } catch (e) {
      toast.error(`Erreur : ${e.message}`);
    } finally {
      setPromoBusy(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  if (!order) return <div className="p-8 text-center text-[#6B6A68]">Commande introuvable.</div>;

  const meta = STATUS_LABELS[order.order_status] || { label: order.order_status, color: 'bg-gray-100 text-gray-700' };
  const canEdit = !['fulfilled', 'shipped', 'delivered', 'livre', 'cancelled'].includes(order.order_status);

  return (
    <div className="min-h-screen bg-[#F9F8F6] p-6 md:p-10" data-testid="admin-order-detail">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Link to="/bloom-gestion-secure/orders" className="inline-flex items-center gap-2 text-sm text-[#6B6A68] hover:text-[#1A1514]">
            <ArrowLeft size={16} /> Retour à la liste
          </Link>
          <span className={`text-xs px-3 py-1.5 font-medium ${meta.color}`}>{meta.label}</span>
        </div>

        <div className="bg-white border border-[#E0DFDD] p-6 md:p-8 space-y-6">
          {/* Title */}
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <h1 className="font-serif text-3xl">Commande #{order.order_id}</h1>
              <p className="text-sm text-[#6B6A68] mt-1">Créée le {formatDateTime(order.created_at)}</p>
              {order.order_type === 'exchange' && (
                <span className="inline-block mt-2 text-xs bg-blue-50 text-blue-700 px-2 py-1">↔ Échange de #{order.exchange_of}</span>
              )}
            </div>
            <p className="font-serif text-3xl">{formatPrice(order.total)}</p>
          </div>

          {/* Customer */}
          <section className="border-t border-[#E0DFDD] pt-5">
            <h2 className="text-xs uppercase tracking-[0.15em] text-[#6B6A68] font-medium mb-3">Client</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <p><span className="text-[#6B6A68]">Nom : </span><span className="font-medium" data-testid="order-customer-name">{order.customer_name}</span></p>
              <p><span className="text-[#6B6A68]">Téléphone : </span><a href={`tel:${order.customer_phone}`} className="font-medium hover:text-[#D4AF37]" data-testid="order-customer-phone">{order.customer_phone}</a></p>
              {order.customer_email && <p className="sm:col-span-2"><span className="text-[#6B6A68]">Email : </span><a href={`mailto:${order.customer_email}`} className="font-medium hover:text-[#D4AF37]">{order.customer_email}</a></p>}
            </div>
          </section>

          {/* Delivery */}
          <section className="border-t border-[#E0DFDD] pt-5">
            <h2 className="text-xs uppercase tracking-[0.15em] text-[#6B6A68] font-medium mb-3">Livraison</h2>
            <div className="space-y-1 text-sm">
              <p><span className="text-[#6B6A68]">Type : </span><span className="font-medium">{order.delivery_type === 'bureau' ? 'Retrait au bureau World Express' : 'Livraison à domicile'}</span></p>
              <p><span className="text-[#6B6A68]">Wilaya : </span>{order.wilaya}</p>
              {order.commune && <p><span className="text-[#6B6A68]">Commune : </span>{order.commune}</p>}
              {order.delivery_type === 'home' && order.shipping_address && <p><span className="text-[#6B6A68]">Adresse : </span>{order.shipping_address}</p>}
              {order.ecotrack_tracking && <p><span className="text-[#6B6A68]">Tracking World Express : </span><span className="font-mono font-medium" data-testid="order-tracking">{order.ecotrack_tracking}</span></p>}
              {order.notes && <p className="bg-[#F5F4F2] p-2 mt-2 italic">Notes cliente : {order.notes}</p>}
            </div>
          </section>

          {/* Admin metadata: social media + internal note */}
          <section className="border-t border-[#E0DFDD] pt-5" data-testid="admin-meta-section">
            <h2 className="text-xs uppercase tracking-[0.15em] text-[#6B6A68] font-medium mb-3">Suivi interne</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-[#6B6A68] flex items-center gap-1.5 mb-1.5">
                  Compte réseau social
                </label>
                {/* Network picker */}
                <div className="flex items-center gap-1 mb-2" data-testid="admin-social-network">
                  {NETWORKS.map(({ value, Icon, label, activeBg }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setSocialNetwork(value === socialNetwork ? '' : value)}
                      className={`inline-flex items-center gap-1 px-2 py-1 text-xs border transition-colors ${
                        socialNetwork === value ? 'text-white border-transparent' : 'text-[#6B6A68] border-[#E0DFDD] hover:border-[#1A1514]'
                      }`}
                      style={socialNetwork === value ? { backgroundColor: activeBg } : {}}
                      title={label}
                      data-testid={`admin-network-${value}`}
                      aria-pressed={socialNetwork === value}
                    >
                      <Icon size={12} />
                      <span>{label}</span>
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={socialMedia}
                    onChange={(e) => setSocialMedia(e.target.value)}
                    placeholder={socialNetwork ? `Nom du compte ${socialNetwork}` : "Sélectionner un réseau puis saisir le compte"}
                    className="flex-1 border border-[#E0DFDD] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#1A1514]"
                    data-testid="admin-social-media-input"
                  />
                  {(() => {
                    const url = resolveSocialUrl(socialMedia, socialNetwork);
                    if (!url) return null;
                    return (
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-3 py-2 border border-[#E0DFDD] hover:border-[#1A1514] hover:text-[#1A1514] text-xs text-[#6B6A68] transition-colors"
                        title={`Ouvrir ${url}`}
                        data-testid="admin-social-open-link"
                      >
                        <ExternalLink size={12} /> Ouvrir
                      </a>
                    );
                  })()}
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-[#6B6A68] flex items-center gap-1.5 mb-1.5">
                  <StickyNote size={12} /> Note interne
                </label>
                <input
                  type="text"
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  placeholder="Rappel, remarque, contexte…"
                  className="w-full border border-[#E0DFDD] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#1A1514]"
                  data-testid="admin-note-input"
                />
              </div>
            </div>
            <button
              onClick={saveMeta}
              disabled={savingMeta || (
                socialMedia === (order.social_media_account || '') &&
                socialNetwork === (order.social_media_network || '') &&
                adminNote === (order.admin_note || '')
              )}
              className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium bg-[#1A1514] text-white hover:bg-[#2a2524] disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="admin-save-meta-btn"
            >
              {savingMeta ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              Enregistrer
            </button>
          </section>

          {/* Items */}
          <section className="border-t border-[#E0DFDD] pt-5">
            <h2 className="text-xs uppercase tracking-[0.15em] text-[#6B6A68] font-medium mb-3">Articles ({order.items?.length || 0})</h2>
            <div className="space-y-2">
              {(order.items || []).map((it, i) => (
                <div key={i} className="flex items-center gap-3 p-3 border border-[#E0DFDD]" data-testid={`detail-item-${i}`}>
                  {it.image_url && <img src={it.image_url} alt={it.name} className="w-14 h-14 object-cover" />}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{it.name}</p>
                    <div className="flex items-center gap-2 mt-1 text-xs">
                      <span className="bg-white px-1.5 py-0.5 border border-[#E0DFDD] font-mono uppercase">{it.size || '—'}</span>
                      {it.color ? (
                        <span className="bg-[#1A1514] text-[#D4AF37] px-1.5 py-0.5 font-medium uppercase tracking-wider">{it.color}</span>
                      ) : (
                        <span className="text-[10px] text-[#9E9C9A] italic">sans couleur</span>
                      )}
                      <span className="text-[10px] text-[#9E9C9A] font-mono">{it.product_id}</span>
                      <span className="text-[#6B6A68]">×{it.quantity}</span>
                    </div>
                  </div>
                  <p className="font-medium text-sm">{formatPrice(it.price * it.quantity)}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Totals */}
          <section className="border-t border-[#E0DFDD] pt-5">
            <div className="space-y-1 text-sm max-w-xs ml-auto">
              <div className="flex justify-between"><span className="text-[#6B6A68]">Sous-total</span><span>{formatPrice(order.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-[#6B6A68]">Livraison</span><span>{order.shipping_cost === 0 ? <span className="text-[#A3B19B] font-medium">GRATUITE</span> : formatPrice(order.shipping_cost)}</span></div>
              {order.promo_code && Number(order.discount_amount || 0) > 0 && (
                <div className="flex justify-between text-[#A3B19B]" data-testid="detail-discount-line">
                  <span>Réduction ({order.promo_code})</span>
                  <span>−{formatPrice(order.discount_amount)}</span>
                </div>
              )}
              <div className="flex justify-between font-semibold pt-1.5 border-t border-[#E0DFDD]"><span>Total</span><span className="font-serif text-base" data-testid="detail-total">{formatPrice(order.total)}</span></div>
            </div>
          </section>

          {/* Promo code — apply/remove BEFORE fulfillment */}
          <section className="border-t border-[#E0DFDD] pt-5" data-testid="admin-promo-section">
            <h2 className="text-xs uppercase tracking-[0.15em] text-[#6B6A68] font-medium mb-3">Code promo</h2>
            {order.promo_code ? (
              <div className="border border-[#E0DFDD] bg-[#F9F8F6] p-4 space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-semibold text-[#1A1514]" data-testid="applied-promo-code">{order.promo_code}</span>
                    {order.discount_details?.kind === 'affiliate' ? (
                      <span className="px-1.5 py-0.5 text-[10px] uppercase tracking-wider bg-[#D4AF37] text-white">Affilié</span>
                    ) : (
                      <span className="px-1.5 py-0.5 text-[10px] uppercase tracking-wider bg-[#C87979] text-white">Promo</span>
                    )}
                  </div>
                  <span className="text-[#A3B19B] font-medium">−{formatPrice(order.discount_amount || 0)}</span>
                </div>
                {order.discount_details && (
                  <ul className="text-xs text-[#6B6A68] space-y-0.5">
                    <li>Type : {order.discount_details.discount_type === 'percentage' ? `${order.discount_details.discount_value}%` : `${formatPrice(order.discount_details.discount_value)} fixe`}</li>
                    <li>Sous-total éligible : {formatPrice(order.discount_details.eligible_subtotal || 0)}</li>
                    {(order.discount_details.excluded_items || []).length > 0 && (
                      <li className="text-[#C87979]">Articles exclus (en promo) : {order.discount_details.excluded_items.join(', ')}</li>
                    )}
                    {order.discount_details.applied_by && (
                      <li>Appliqué par : {order.discount_details.applied_by}</li>
                    )}
                  </ul>
                )}
                {canEdit && (
                  <button
                    onClick={() => window.confirm('Retirer le code promo ? Le total sera recalculé sans réduction.') && applyPromo(null)}
                    disabled={promoBusy}
                    className="mt-2 px-3 py-1.5 text-xs font-medium border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50"
                    data-testid="btn-remove-promo"
                  >
                    ✖ Retirer le code
                  </button>
                )}
              </div>
            ) : canEdit ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={promoInput}
                  onChange={(e) => setPromoInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && promoInput.trim()) applyPromo(promoInput.trim()); }}
                  placeholder="ex : BLOOM30"
                  className="checkout-input flex-1 uppercase text-sm"
                  data-testid="promo-input"
                />
                <button
                  onClick={() => promoInput.trim() && applyPromo(promoInput.trim())}
                  disabled={promoBusy || !promoInput.trim()}
                  className="px-4 py-2 text-xs font-medium bg-[#1A1514] text-white hover:bg-black disabled:opacity-50"
                  data-testid="btn-apply-promo"
                >
                  {promoBusy ? 'Application...' : 'Appliquer & recalculer'}
                </button>
              </div>
            ) : (
              <p className="text-xs text-[#9E9C9A] italic">Aucun code promo. Modification impossible après expédition.</p>
            )}
          </section>

          {/* Actions */}
          <section className="border-t border-[#E0DFDD] pt-5">
            <h2 className="text-xs uppercase tracking-[0.15em] text-[#6B6A68] font-medium mb-3">Actions</h2>
            <div className="flex flex-wrap gap-2">
              {(order.order_status === 'pending' || order.order_status === 'en_attente') && (
                <button onClick={() => window.confirm('Confirmer après avoir parlé à la cliente ?') && callWorkflow('confirm')} className="px-4 py-2 text-xs font-medium bg-blue-600 text-white hover:bg-blue-700" data-testid="btn-confirm">✓ Confirmer</button>
              )}
              {!['fulfilled', 'shipped', 'delivered', 'livre', 'cancelled'].includes(order.order_status) && (
                <button onClick={() => window.confirm('Préparer & expédier vers World Express ?') && callWorkflow('fulfill')} className="px-4 py-2 text-xs font-medium bg-purple-600 text-white hover:bg-purple-700" data-testid="btn-fulfill">📦 Préparer & expédier</button>
              )}
              {(order.order_status === 'fulfilled' || order.order_status === 'shipped') && order.ecotrack_tracking && (
                <a href={`/api/bloom-gestion-secure/shipping/${order.order_id}/label`} target="_blank" rel="noopener noreferrer" className="px-4 py-2 text-xs font-medium border border-[#1A1514] hover:bg-[#F9F8F6] inline-flex items-center gap-1.5">📄 Étiquette <ExternalLink size={12} /></a>
              )}
              {canEdit && <button onClick={() => setShowEdit(true)} className="px-4 py-2 text-xs font-medium border border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="btn-edit-items">✏️ Modifier les articles</button>}
              {order.order_type !== 'exchange' && !order.has_exchange && <button onClick={() => setShowExchange(true)} className="px-4 py-2 text-xs font-medium border border-blue-300 text-blue-600 hover:bg-blue-50" data-testid="btn-exchange">🔄 Échanger</button>}
              {order.customer_phone && (
                <a href={buildWhatsAppUrl(order, 'auto')} target="_blank" rel="noopener noreferrer" className="px-4 py-2 text-xs font-medium bg-green-600 text-white hover:bg-green-700 inline-flex items-center gap-1.5" data-testid="btn-whatsapp">💬 WhatsApp</a>
              )}
              {order.order_status !== 'cancelled' && order.order_status !== 'fulfilled' && order.order_status !== 'shipped' && (
                <button onClick={() => {
                  const reason = window.prompt('Raison de l\'annulation ?') ?? '';
                  if (window.confirm('Annuler cette commande ? Le stock sera restauré.')) callWorkflow('cancel', { reason });
                }} className="px-4 py-2 text-xs font-medium border border-red-300 text-red-600 hover:bg-red-50" data-testid="btn-cancel">✖ Annuler</button>
              )}
            </div>
          </section>

          {/* History */}
          {history.length > 0 && (
            <section className="border-t border-[#E0DFDD] pt-5">
              <h2 className="text-xs uppercase tracking-[0.15em] text-[#6B6A68] font-medium mb-3">Journal des modifications ({history.length})</h2>
              <ul className="divide-y divide-[#E0DFDD] border border-[#E0DFDD]">
                {history.slice().reverse().map((h, i) => {
                  const actionLabels = {
                    add_item: 'Ajout',
                    remove_item: 'Suppression',
                    update_qty: 'Quantité',
                    apply_promo: 'Code promo appliqué',
                    remove_promo: 'Code promo retiré',
                  };
                  const isPromo = h.action === 'apply_promo' || h.action === 'remove_promo';
                  return (
                  <li key={i} className="p-3 text-xs">
                    <p>
                      <span className="font-medium">{actionLabels[h.action] || h.action}</span>
                      <span className="text-[#6B6A68]"> — {formatDateTime(h.ts)} • {h.by}</span>
                    </p>
                    <p className="text-[#6B6A68] mt-0.5">
                      {isPromo ? (
                        h.action === 'apply_promo'
                          ? `${h.details?.code} · −${formatPrice(h.details?.discount || 0)} · Total ${formatPrice(h.details?.previous_total || 0)} → ${formatPrice(h.details?.new_total || 0)}`
                          : `Précédent : ${h.details?.previous_code || '—'} (−${formatPrice(h.details?.previous_discount || 0)})`
                      ) : (
                        <>
                          {h.details?.name}
                          {h.details?.size ? ` (${h.details.size}${h.details?.color ? ` / ${h.details.color}` : ''})` : ''}
                          {' • '}
                          {h.action === 'update_qty' ? `${h.details?.old_qty} → ${h.details?.new_qty}` : `qté ${h.details?.quantity}`}
                        </>
                      )}
                    </p>
                  </li>
                  );
                })}
              </ul>
            </section>
          )}
        </div>
      </div>

      {showEdit && order && (
        <EditOrderItemsModal order={order} onClose={() => setShowEdit(false)} onSuccess={() => { fetchOrder(); fetchHistory(); }} />
      )}
      {showExchange && order && (
        <ExchangeModal order={order} onClose={() => setShowExchange(false)} onSuccess={fetchOrder} />
      )}
    </div>
  );
}
